/**
 * API client — Module Cahier des Charges (Spec)
 * All calls require a valid JWT (Bearer token via cookies / Authorization header).
 */

import { getAuthToken } from '../store';
import type {
  Client,
  Specification,
  SpecificationSection,
  FunctionalRequirement,
  TechnicalRequirement,
  NonFunctionalRequirement,
  Deliverable,
  ProjectConstraint,
} from '../types/specification';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const headers = new Headers(options.headers || {});
  if (!(options.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  const token = getAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_URL}/spec${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
};

// ─── Backend response shapes ──────────────────────────────────────────────────

interface BClient {
  id: string; userId: string; companyName: string; logo?: string | null;
  brandPrimaryColor?: string | null; brandSecondaryColor?: string | null;
  address: string; phone: string; email: string;
  contactPerson: string; contactRole?: string | null;
  sector: string; siret?: string | null; website?: string | null; notes?: string | null;
  createdAt: string; updatedAt: string;
}

interface BRequirement {
  id: string; documentId: string; type: string; code: string;
  title: string; description: string; priority: string; category: string;
  mandatory: boolean; acceptanceCriteria: unknown[]; estimatedComplexity?: string | null;
  createdAt: string; updatedAt: string;
}

interface BSection {
  id: string; documentId: string; title: string; content: string;
  order: number; subsections: unknown[]; createdAt: string; updatedAt: string;
}

interface BDeliverable {
  id: string; documentId: string; name: string; description: string;
  dueDate?: string | null; responsible?: string | null;
  createdAt: string; updatedAt: string;
}

interface BConstraint {
  id: string; documentId: string; type: string; description: string;
  impact: string; createdAt: string; updatedAt: string;
}

interface BDocument {
  id: string; clientId: string; projectId?: string | null; createdBy: string;
  title: string; reference: string; version: string; status: string;
  projectContext: string; projectObjectives: string; projectScope: string;
  estimatedStartDate?: string | null; estimatedEndDate?: string | null;
  estimatedBudget?: number | null; currency: string; stakeholders: unknown[];
  notes?: string | null; approvedBy?: string | null; approvedAt?: string | null;
  signedBy?: string | null; signedAt?: string | null;
  createdAt: string; updatedAt: string;
  client?: BClient;
  sections?: BSection[];
  requirements?: BRequirement[];
  deliverables?: BDeliverable[];
  constraints?: BConstraint[];
}

// ─── Mappers backend → frontend ──────────────────────────────────────────────

const STATUS_MAP: Record<string, string> = {
  DRAFT: 'draft', IN_REVIEW: 'in_review', APPROVED: 'approved',
  SIGNED: 'signed', ARCHIVED: 'archived',
};

const PRIORITY_MAP: Record<string, string> = {
  LOW: 'low', MEDIUM: 'medium', HIGH: 'high', CRITICAL: 'critical',
};

export const mapClient = (b: BClient): Client => ({
  id: b.id,
  companyName: b.companyName,
  logo: b.logo ?? undefined,
  brandPrimaryColor: b.brandPrimaryColor ?? undefined,
  brandSecondaryColor: b.brandSecondaryColor ?? undefined,
  address: b.address,
  phone: b.phone,
  email: b.email,
  contactPerson: b.contactPerson,
  contactRole: b.contactRole ?? undefined,
  sector: b.sector,
  siret: b.siret ?? undefined,
  website: b.website ?? undefined,
  notes: b.notes ?? undefined,
  createdAt: new Date(b.createdAt),
  updatedAt: new Date(b.updatedAt),
});

const mapSection = (s: BSection): SpecificationSection => ({
  id: s.id,
  title: s.title,
  content: s.content,
  order: s.order,
  subsections: Array.isArray(s.subsections)
    ? (s.subsections as SpecificationSection['subsections'])
    : [],
});

const mapFuncReq = (r: BRequirement): FunctionalRequirement => ({
  id: r.id,
  code: r.code,
  title: r.title,
  description: r.description,
  priority: (PRIORITY_MAP[r.priority] ?? r.priority.toLowerCase()) as FunctionalRequirement['priority'],
  category: r.category,
  acceptanceCriteria: (r.acceptanceCriteria as string[]) ?? [],
  estimatedComplexity: (r.estimatedComplexity as FunctionalRequirement['estimatedComplexity']) ?? undefined,
});

const mapTechReq = (r: BRequirement): TechnicalRequirement => ({
  id: r.id,
  code: r.code,
  title: r.title,
  description: r.description,
  category: r.category,
  mandatory: r.mandatory,
});

const mapNfReq = (r: BRequirement): NonFunctionalRequirement => ({
  id: r.id,
  code: r.code,
  title: r.title,
  description: r.description,
  category: (r.category as NonFunctionalRequirement['category']) ?? 'other',
  priority: (PRIORITY_MAP[r.priority] ?? r.priority.toLowerCase()) as NonFunctionalRequirement['priority'],
  metric: (r as unknown as Record<string, unknown>).metric as string | undefined,
  acceptanceCriteria: (r.acceptanceCriteria as string[]) ?? [],
});

const mapDeliverable = (d: BDeliverable): Deliverable => ({
  id: d.id,
  name: d.name,
  description: d.description,
  dueDate: d.dueDate ? new Date(d.dueDate) : undefined,
  responsible: d.responsible ?? undefined,
});

const mapConstraint = (c: BConstraint): ProjectConstraint => ({
  id: c.id,
  type: c.type as ProjectConstraint['type'],
  description: c.description,
  impact: c.impact as ProjectConstraint['impact'],
});

export const mapDocument = (b: BDocument): Specification => {
  const requirements = b.requirements ?? [];
  const funcReqs = requirements.filter((r) => r.type === 'functional');
  const techReqs = requirements.filter((r) => r.type === 'technical');
  const nfReqs = requirements.filter((r) => r.type === 'non_functional');

  return {
    id: b.id,
    clientId: b.clientId,
    projectId: b.projectId ?? undefined,
    title: b.title,
    reference: b.reference,
    version: b.version,
    status: (STATUS_MAP[b.status] ?? b.status.toLowerCase()) as Specification['status'],
    projectContext: b.projectContext,
    projectObjectives: b.projectObjectives,
    projectScope: b.projectScope,
    sections: (b.sections ?? []).map(mapSection),
    functionalRequirements: funcReqs.map(mapFuncReq),
    technicalRequirements: techReqs.map(mapTechReq),
    nonFunctionalRequirements: nfReqs.map(mapNfReq),
    constraints: (b.constraints ?? []).map(mapConstraint),
    deliverables: (b.deliverables ?? []).map(mapDeliverable),
    estimatedStartDate: b.estimatedStartDate ? new Date(b.estimatedStartDate) : undefined,
    estimatedEndDate: b.estimatedEndDate ? new Date(b.estimatedEndDate) : undefined,
    estimatedBudget: b.estimatedBudget ?? undefined,
    currency: b.currency,
    stakeholders: (b.stakeholders as Specification['stakeholders']) ?? [],
    notes: b.notes ?? undefined,
    createdBy: b.createdBy,
    createdAt: new Date(b.createdAt),
    updatedAt: new Date(b.updatedAt),
    approvedBy: b.approvedBy ?? undefined,
    approvedAt: b.approvedAt ? new Date(b.approvedAt) : undefined,
    signedBy: b.signedBy ?? undefined,
    signedAt: b.signedAt ? new Date(b.signedAt) : undefined,
  };
};

// ─── Clients ─────────────────────────────────────────────────────────────────

export const apiListClients = (): Promise<{ clients: BClient[] }> =>
  request('/clients');

export const apiCreateClient = (data: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ client: BClient }> =>
  request('/clients', { method: 'POST', body: JSON.stringify(data) });

export const apiUpdateClient = (id: string, data: Partial<Client>): Promise<{ client: BClient }> =>
  request(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const apiDeleteClient = (id: string): Promise<{ ok: boolean }> =>
  request(`/clients/${id}`, { method: 'DELETE' });

// ─── Documents ───────────────────────────────────────────────────────────────

export const apiListDocuments = (params?: { clientId?: string; projectId?: string; status?: string; q?: string }): Promise<{ documents: BDocument[] }> => {
  const qs = params ? '?' + new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]
  ).toString() : '';
  return request(`/documents${qs}`);
};

export const apiGetDocument = (id: string): Promise<{ document: BDocument }> =>
  request(`/documents/${id}`);

export const apiCreateDocument = (data: {
  clientId: string; title: string; projectId?: string; version?: string;
  projectContext?: string; projectObjectives?: string; projectScope?: string;
  estimatedStartDate?: string; estimatedEndDate?: string; estimatedBudget?: number;
  currency?: string; stakeholders?: unknown[]; notes?: string;
}): Promise<{ document: BDocument }> =>
  request('/documents', { method: 'POST', body: JSON.stringify(data) });

export const apiUpdateDocument = (id: string, data: Record<string, unknown>): Promise<{ document: BDocument }> =>
  request(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const apiDeleteDocument = (id: string): Promise<{ ok: boolean }> =>
  request(`/documents/${id}`, { method: 'DELETE' });

export const apiDuplicateDocument = (id: string): Promise<{ document: BDocument }> =>
  request(`/documents/${id}/duplicate`, { method: 'POST' });

export const apiUpdateDocumentStatus = (id: string, status: string, by?: string): Promise<{ document: BDocument }> =>
  request(`/documents/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status, by }) });

// ─── Sections ────────────────────────────────────────────────────────────────

export const apiCreateSection = (docId: string, data: { title: string; content?: string; order?: number; subsections?: unknown[] }): Promise<{ section: BSection }> =>
  request(`/documents/${docId}/sections`, { method: 'POST', body: JSON.stringify(data) });

export const apiUpdateSection = (docId: string, sectionId: string, data: Partial<SpecificationSection>): Promise<{ section: BSection }> =>
  request(`/documents/${docId}/sections/${sectionId}`, { method: 'PATCH', body: JSON.stringify(data) });

export const apiDeleteSection = (docId: string, sectionId: string): Promise<{ ok: boolean }> =>
  request(`/documents/${docId}/sections/${sectionId}`, { method: 'DELETE' });

export const apiReorderSections = (docId: string, sectionIds: string[]): Promise<{ ok: boolean }> =>
  request(`/documents/${docId}/sections/reorder`, { method: 'POST', body: JSON.stringify({ sectionIds }) });

// ─── Requirements ─────────────────────────────────────────────────────────────

export const apiCreateRequirement = (docId: string, data: Record<string, unknown>): Promise<{ requirement: BRequirement }> =>
  request(`/documents/${docId}/requirements`, { method: 'POST', body: JSON.stringify(data) });

export const apiUpdateRequirement = (docId: string, reqId: string, data: Record<string, unknown>): Promise<{ requirement: BRequirement }> =>
  request(`/documents/${docId}/requirements/${reqId}`, { method: 'PATCH', body: JSON.stringify(data) });

export const apiDeleteRequirement = (docId: string, reqId: string): Promise<{ ok: boolean }> =>
  request(`/documents/${docId}/requirements/${reqId}`, { method: 'DELETE' });

// ─── Deliverables ─────────────────────────────────────────────────────────────

export const apiCreateDeliverable = (docId: string, data: Record<string, unknown>): Promise<{ deliverable: BDeliverable }> =>
  request(`/documents/${docId}/deliverables`, { method: 'POST', body: JSON.stringify(data) });

export const apiUpdateDeliverable = (docId: string, delivId: string, data: Record<string, unknown>): Promise<{ deliverable: BDeliverable }> =>
  request(`/documents/${docId}/deliverables/${delivId}`, { method: 'PATCH', body: JSON.stringify(data) });

export const apiDeleteDeliverable = (docId: string, delivId: string): Promise<{ ok: boolean }> =>
  request(`/documents/${docId}/deliverables/${delivId}`, { method: 'DELETE' });

// ─── Constraints ──────────────────────────────────────────────────────────────

export const apiCreateConstraint = (docId: string, data: Record<string, unknown>): Promise<{ constraint: BConstraint }> =>
  request(`/documents/${docId}/constraints`, { method: 'POST', body: JSON.stringify(data) });

export const apiUpdateConstraint = (docId: string, constraintId: string, data: Record<string, unknown>): Promise<{ constraint: BConstraint }> =>
  request(`/documents/${docId}/constraints/${constraintId}`, { method: 'PATCH', body: JSON.stringify(data) });

export const apiDeleteConstraint = (docId: string, constraintId: string): Promise<{ ok: boolean }> =>
  request(`/documents/${docId}/constraints/${constraintId}`, { method: 'DELETE' });
